import {
  NetworkTopologyClusterElementsEnum,
  NetworkTopologyElementsEnum
} from 'data/network-topology/network-topology.types';
import L from 'leaflet';
import _ from 'lodash';
import MapHelper, { MarkerSummary, NeEdge } from './MapHelper';

export interface TooltipRow {}

export interface TooltipLabelValue extends TooltipRow {
  label: string,
  value: any
}

export interface TooltipCaptionText extends TooltipRow {
  caption: string
}

const getTooltipData = (
  edge: NeEdge,
  mapHelper: MapHelper
): TooltipRow[] => {
  if (edge.sourceType === NetworkTopologyElementsEnum.AccessFunction || edge.sourceType === NetworkTopologyClusterElementsEnum.AccessFunctionCluster) {
    return getAfTooltipData(edge, mapHelper);
  }
  if (edge.sourceType === NetworkTopologyElementsEnum.Xcipio || edge.sourceType === NetworkTopologyClusterElementsEnum.XcipioCluster) {
    return getXcipioTooltipData(edge, mapHelper);
  }
  return [];
};

const getAfTooltipData = (edge: NeEdge, mapHelper: MapHelper) => {
  const ret = [];
  if (edge.sourceType === NetworkTopologyElementsEnum.AccessFunction) {
    const { leafletId } = mapHelper.afIdToLeafletIdMap[edge.sourceId];
    const marker = mapHelper.getMarkerByLeafletId(leafletId);
    if (marker.neDetails) {
      const sourceIp = getAfSourceIp(marker.neDetails);
      ret.push({ label: 'POI ID', value: marker.neDetails.data.id });
      if (sourceIp?.value) {
        ret.push({ label: 'POI IP', value: sourceIp.value });
      }
    }
  }
  if (edge.sourceType === NetworkTopologyClusterElementsEnum.AccessFunctionCluster) {
    const statusCount = edge.statuses?.filter((s) => edge.status === s).reduce((acc, status) => {
      acc += 1;
      return acc;
    }, 0);
    ret.push({
      caption: `${statusCount} POI links are ${_.startCase(edge.status.toLowerCase())}`
    });
  }
  if (edge.destinationType === NetworkTopologyElementsEnum.Xcipio) {
    const marker = mapHelper.getMarkerByLeafletId(edge.destinationLeafletId);
    const node = marker.neDetails;
    if (node) {
      ret.push({ label: `${node.data.nodeType} ID`, value: node.data.id });
      ret.push({ label: `${node.data.nodeType} IP`, value: node.data.ipAddress });
    }
  }

  if (edge.sourceType === NetworkTopologyElementsEnum.AccessFunction && edge.destinationType === NetworkTopologyClusterElementsEnum.XcipioCluster) {
    const childDetails = mapHelper.getChildDataByLeafletId(edge.destinationLeafletId);
    const node = childDetails.find((c) => c.data.id === edge.destinationId);
    if (node) {
      ret.push({ label: `${node.data.nodeType} ID`, value: node.data.id });
      ret.push({ label: `${node.data.nodeType} IP`, value: node.data.ipAddress });
    }

  }
  return ret;
}

const getAfSourceIp = (ne: any) => {
  let sourceIp;
    if (
      ne.data.provisioningInterfaces &&
      ne.data.provisioningInterfaces.length > 0
    ) {
      sourceIp = ne.data.provisioningInterfaces[0].options.find(
        (o: any) => o.key === 'destIPAddr'
      );
      if (!sourceIp) {
        sourceIp = ne.data.provisioningInterfaces[0].options.find(
          (o: any) => o.key === 'uri'
        );
      }
    }
    return sourceIp;
}

const getXcipioTooltipData = (edge: NeEdge, mapHelper: MapHelper) => {
  const ret = [];

  if (edge.sourceType === NetworkTopologyElementsEnum.Xcipio) {
    const marker = mapHelper.getMarkerByLeafletId(edge.sourceLeafletId);
    if (marker.neDetails) {
      ret.push({
        label: `${marker.neDetails.data.nodeType} ID`,
        value: marker.neDetails.data.id
      });
      ret.push({
        label: `${marker.neDetails.data.nodeType} IP`,
        value: marker.neDetails.data.ipAddress
      });
    }
  }

  if (edge.destinationType === NetworkTopologyElementsEnum.Xcipio) {
    const marker = mapHelper.getMarkerByLeafletId(edge.destinationLeafletId);
    if (marker.neDetails) {
      ret.push({
        label: `${marker.neDetails.data.nodeType} ID`,
        value: marker.neDetails.data.id
      });
      ret.push({
        label: `${marker.neDetails.data.nodeType} IP`,
        value: marker.neDetails.data.ipAddress
      });
    }
  }

  if (edge.sourceType === NetworkTopologyElementsEnum.Xcipio && edge.destinationType === NetworkTopologyClusterElementsEnum.XcipioCluster) {
    const childDetails = mapHelper.getChildDataByLeafletId(edge.destinationLeafletId);
    const node = childDetails.find((c) => c.data.id === edge.destinationId);
    if (node) {
      ret.push({ label: `${node.data.nodeType} ID`, value: node.data.id });
      ret.push({ label: `${node.data.nodeType} IP`, value: node.data.ipAddress });
    }
  }

  if (edge.destinationType === NetworkTopologyElementsEnum.CollectionFunction) {
    const marker = mapHelper.getMarkerByLeafletId(edge.destinationLeafletId);
    let cfIpAddress;
    let cfVersion;
    if (marker.neDetails?.data.hi2Interfaces && marker.neDetails?.data.hi2Interfaces.length > 0) {
      cfIpAddress = marker.neDetails?.data.hi2Interfaces[0]?.options?.find((x: any) => x.key === 'destIPAddr').value;
      cfVersion = marker.neDetails?.data.hi2Interfaces[0]?.options?.find((x: any) => x.key === 'version').value;
    }
    if (marker.neDetails && marker.neDetails.data.id) {
      ret.push({ label: 'CF ID', value: marker.neDetails.data.id });
    }
    if (cfIpAddress) {
      ret.push({ label: 'CF IP', value: cfIpAddress });
    }
    if (cfVersion) {
      ret.push({ label: 'CF Version', value: cfVersion });
    }
  }

  if (edge.destinationType === NetworkTopologyElementsEnum.AccessFunction) {
    const marker = mapHelper.getMarkerByLeafletId(edge.destinationLeafletId);
    if (marker.neDetails) {
      const sourceIp = getAfSourceIp(marker.neDetails);
      ret.push({ label: 'POI ID', value: marker.neDetails.data.id });
      if (sourceIp.value) {
        ret.push({ label: 'POI IP', value: sourceIp.value });
      }
    }
  }

  if (edge.destinationType === NetworkTopologyClusterElementsEnum.AccessFunctionCluster) {
    const marker = mapHelper.afLeafletIdToMarkerMap[edge.destinationLeafletId];
    const statusCount = edge.statuses?.reduce((acc, status) => {
      if (!acc[status]) {
        acc[status] = 1;
      } else {
        acc[status] += 1;
      }
      return acc;
    }, {} as any);
    const sortedStatuses = sortStatusMap(statusCount);
    sortedStatuses.forEach((s) => {
      ret.push({
        caption: `${s.count} POI links are ${_.startCase(s.status.toLowerCase())}`
      });
    });
  }

  if (edge.destinationType === NetworkTopologyClusterElementsEnum.CollectionFunctionCluster) {
    const statusCount = edge.statuses?.reduce((acc, status) => {
      if (!acc[status]) {
        acc[status] = 1;
      } else {
        acc[status] += 1;
      }
      return acc;
    }, {} as any);
    const sortedStatuses = sortStatusMap(statusCount);
    sortedStatuses.forEach((s) => {
      ret.push({
        caption: `${s.count} CF links are ${_.startCase(s.status.toLowerCase())}`
      });
    });
  }
  return ret;
}

const sortStatusMap = (statusCount: any): { status: string, count: number }[] => {
  const ret = Object.entries(statusCount).map((s) => {
    return { status: s[0], count: s[1] as number };
  });

  ret.sort((a, b) => {
    if (a.status < b.status) {
      return -1;
    }
    if (a.status > b.status) {
      return 1;
    }
    return 0;
  });
  return ret;
}

export const createClusterPopup = (leafletId: string, mapHelper: MapHelper, handleViewMore: any): HTMLDivElement => {
  const marker = mapHelper.getMarkerByLeafletId(leafletId);
  if (marker.childCount && marker.childCount <= 5) {
    return createClusterDetailPopup(marker, handleViewMore);
  }
  return createClusterStatusPopup(marker, handleViewMore);
}

const isAf = (type: NetworkTopologyElementsEnum | NetworkTopologyClusterElementsEnum): boolean => {
  return (
    type === NetworkTopologyElementsEnum.AccessFunction ||
    type === NetworkTopologyClusterElementsEnum.AccessFunctionCluster
  );
}

const isCf = (type: NetworkTopologyElementsEnum | NetworkTopologyClusterElementsEnum): boolean => {
  return (
    type === NetworkTopologyElementsEnum.CollectionFunction ||
    type === NetworkTopologyClusterElementsEnum.CollectionFunctionCluster
  );
}

const isXcipio = (type: NetworkTopologyElementsEnum | NetworkTopologyClusterElementsEnum): boolean => {
  return (
    type === NetworkTopologyElementsEnum.Xcipio ||
    type === NetworkTopologyClusterElementsEnum.XcipioCluster
  );
}

const isCCPAG = (type: NetworkTopologyElementsEnum | NetworkTopologyClusterElementsEnum): boolean => {
  return (
    type === NetworkTopologyElementsEnum.CCPAG ||
    type === NetworkTopologyClusterElementsEnum.CCPAGCluster
  );
}

export const createClusterDetailPopup = (markerSummary: MarkerSummary, handleViewMore: any): HTMLDivElement => {
  const children = (markerSummary.childNeData || []).map((x: any) => {
    return {
      id: x.data.id,
      name: (isAf(markerSummary.markerType) || isCf(markerSummary.markerType)) ? x.data.name : x.data.nodeName,
      status: x.status
    };
  });
  const popupContainer = L.DomUtil.create('div', 'popup-cluster-container');
  children.forEach((c) => {
    const afDetailRow = L.DomUtil.create('div', 'cluster-af-detail-row', popupContainer);

    const afDetailRowStatusNameDiv = L.DomUtil.create('div', 'cluster-af-detail-row-status-name', afDetailRow);
    const afDetailStatusContainerDiv = L.DomUtil.create('div', undefined, afDetailRowStatusNameDiv);
    L.DomUtil.create('span', getStatusIconClass(c.status), afDetailStatusContainerDiv);
    if (c.status === 'LEADER') {
      const afLeaderADMFSpan = L.DomUtil.create('span', 'cluster-detail-row-admf-leader', afDetailRowStatusNameDiv);
      afLeaderADMFSpan.setAttribute('title', 'MASTER');
      afLeaderADMFSpan.innerHTML = 'M';
    }
    const afDetailNameSpan = L.DomUtil.create('span', 'cluster-af-detail-row-name', afDetailRowStatusNameDiv);
    afDetailNameSpan.setAttribute('title', c.name);
    afDetailNameSpan.innerHTML = c.name;

    const afDetailIdDiv = L.DomUtil.create('div', 'cluster-af-detail-row-id', afDetailRow);
    const afDetailIDSpan = L.DomUtil.create('span', '', afDetailIdDiv);
    afDetailIDSpan.setAttribute('title', c.id);
    afDetailIDSpan.innerHTML = `ID: ${c.id}`;
  });
  const popupFooter = L.DomUtil.create('div', 'popup-cluster-footer', popupContainer);
  const navigationLink = L.DomUtil.create('a', 'bp3-minimal bp3-button bp3-intent-primary', popupFooter);
  navigationLink.setAttribute('role', 'button');
  navigationLink.innerHTML = 'Open Cluster';
  L.DomEvent.on(navigationLink, 'click', () => {
    handleViewMore();
  });
  return popupContainer;
};

export const createClusterStatusPopup = (markerSummary: MarkerSummary, handleViewMore: any): HTMLDivElement => {
  const statusCountMap = (markerSummary.childNeData || [])
  .map((d) => d.status)
  .reduce((acc, curr) => {
    if (!acc[curr]) {
      acc[curr] = 1;
    } else {
      acc[curr] += 1;
    }
    return acc;
  }, {} as any);

  const sortedStatuses = sortStatusMap(statusCountMap);
  const popupContainer = L.DomUtil.create('div', 'popup-cluster-container');
  const tableHeader = L.DomUtil.create('div', 'popup-cluster-container-af-table-header', popupContainer);
  let typeStr = 'POIs';
  if (isCf(markerSummary.markerType)) {
    typeStr = 'CFs';
  }
  if (isXcipio(markerSummary.markerType)) {
    typeStr = 'Xcipios'
  }
  if (isCCPAG(markerSummary.markerType)) {
    typeStr = 'CCPAGs';
  }
  // Header columns
  const header1 = L.DomUtil.create('span', undefined, tableHeader);
  const header2 = L.DomUtil.create('span', undefined, tableHeader);
  header1.innerHTML = `Status of ${typeStr}`;
  header2.innerHTML = 'Total';

  sortedStatuses.forEach((row) => {
    const tableRow = L.DomUtil.create('div', 'popup-cluster-container-af-table-row', popupContainer);
    const afStatusIconContainerDiv = L.DomUtil.create('div', undefined, tableRow);
    L.DomUtil.create('span', getStatusIconClass(row.status), afStatusIconContainerDiv);
    const statusSpan = L.DomUtil.create('span', 'popup-cluster-container-col-table-status', tableRow);
    statusSpan.innerHTML = _.startCase(row.status.toLowerCase());
    L.DomUtil.create('div', 'flex-spacer', tableRow);
    const totalDiv = L.DomUtil.create('span', 'popup-cluster-container-col-table-total', tableRow);
    totalDiv.innerHTML = statusCountMap[row.status];
  });

  const popupFooter = L.DomUtil.create('div', 'popup-cluster-footer', popupContainer);
  const navigationLink = L.DomUtil.create('a', 'bp3-minimal bp3-button bp3-intent-primary', popupFooter);
  navigationLink.setAttribute('role', 'button');
  navigationLink.innerHTML = 'Open Cluster';
  L.DomEvent.on(navigationLink, 'click', () => {
    handleViewMore();
  });
  return popupContainer;
};

const getStatusIconClass = (status: string) => {
  let ret = 'cluster-row-failed';
  if (status === 'ACTIVE' || status === 'LEADER') {
    ret = 'cluster-row-active';
  } else if (status === 'INACTIVE') {
    ret = 'cluster-row-inactive';
  } else if (status === 'UNKNOWN') {
    ret = 'cluster-row-unknown';
  } else if (status === 'FAILED') {
    ret = 'cluster-row-failed';
  }
  return ret;
};

export const createNeRowItem = (popupContainerNe: HTMLDivElement, titleText: string, value: string) => {
  const neRowItem = L.DomUtil.create('div', 'ne-row-item', popupContainerNe);
  const neRowItemLabel = L.DomUtil.create('span', 'ne-row-item-label', neRowItem);
  neRowItemLabel.innerHTML = titleText;
  const neRowItemValue = L.DomUtil.create('span', 'ne-row-item-value', neRowItem);
  neRowItemValue.setAttribute('title', value);
  neRowItemValue.innerHTML = value;
}

export function createRowHeader(popupContainerNe: HTMLDivElement, details: any, name: any) {
  const neRowHeader = L.DomUtil.create('div', 'ne-row-header', popupContainerNe);
  const statusSpanDiv = L.DomUtil.create('div', undefined, neRowHeader);
  L.DomUtil.create('span', getStatusIconClass(details.status), statusSpanDiv);
  const afDetailNameSpan = L.DomUtil.create('span', undefined, neRowHeader);
  afDetailNameSpan.setAttribute('title', name);
  afDetailNameSpan.innerHTML = name;
}

export default getTooltipData;
